/**
 * SkillFab Core Runtime
 * Global functions available on all pages
 */

(function() {
  'use strict';

  // Base API URL — empty string means same origin (works for both dev and prod)
  window.API = window.API || '';

  // Unified core-ready and auth-state registries. Page scripts must use
  // page-runtime.ts, which registers through these APIs instead of mutating
  // page-specific globals.
  const readyCallbacks: Array<() => void | Promise<void>> = [];
  type AuthStatus = 'pending' | 'anonymous' | 'authenticated' | 'error';

  const authStateHandlers: Array<(loggedIn: boolean, user: typeof window.currentUser) => void | Promise<void>> = [];
  let coreReady = !!window.__skillfabCoreReady;
  let authResolved = false;
  let lastLoggedIn = false;

  window.onSkillfabCoreReady = function(callback: () => void | Promise<void>): void {
    if (typeof callback !== 'function') return;
    if (coreReady) {
      void callback();
      return;
    }
    readyCallbacks.push(callback);
  };

  window.registerAuthStateHandler = function(handler: (loggedIn: boolean, user: typeof window.currentUser) => void | Promise<void>): void {
    if (typeof handler !== 'function') return;
    authStateHandlers.push(handler);
    if (authResolved) {
      void handler(lastLoggedIn, window.currentUser);
    }
  };

  window.markSkillfabCoreReady = function(): void {
    coreReady = true;
    window.__skillfabCoreReady = true;
    while (readyCallbacks.length) {
      const callback = readyCallbacks.shift();
      try {
        void callback!();
      } catch (err) {
        console.error('SkillFab core ready callback failed:', err);
      }
    }
  };

  // Auth state management (expose as global for inline scripts).
  // Status is explicit so pages can distinguish "checking token" from truly signed out.
  window.authToken = localStorage.getItem('skillfab_token');
  window.currentUser = null;
  window.authStatus = 'pending';

  function notifyAuthState(loggedIn: boolean): void {
    authResolved = true;
    lastLoggedIn = loggedIn;
    authStateHandlers.forEach(function(handler) {
      try {
        void handler(loggedIn, window.currentUser);
      } catch (err) {
        console.error('SkillFab auth state handler failed:', err);
      }
    });
  }

  window.applyAuthVisibility = function(status?: AuthStatus): void {
    const currentStatus = status || window.authStatus || 'pending';
    document.querySelectorAll<HTMLElement>('[data-auth-visible]').forEach(function(el) {
      const rules = (el.getAttribute('data-auth-visible') || '').split(/\s+/).filter(Boolean);
      const roleRule = window.currentUser && rules.includes(window.currentUser.role);
      const shouldShow = rules.includes(currentStatus) || !!roleRule;
      el.style.display = shouldShow ? (el.getAttribute('data-auth-display') || '') : 'none';
      el.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      if (el.classList.contains('hidden')) {
        el.classList.toggle('hidden', !shouldShow);
      }
    });
  };

  window.applyAuthVisibility(window.authStatus);

  function buildCoreAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (window.authToken) headers['Authorization'] = 'Bearer ' + window.authToken;
    return headers;
  }

  window.checkAuth = async function(): Promise<void> {
    try {
      const res = await fetch(window.API + '/api/auth/me', {
        headers: buildCoreAuthHeaders(),
        credentials: 'same-origin',
      });
      if (res.ok) {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          window.currentUser = data.data;
          window.updateNavAuth(true);
        } catch (_parseErr) {
          console.error('checkAuth: invalid JSON from /api/auth/me');
          window.authStatus = 'error';
          window.applyAuthVisibility(window.authStatus);
          notifyAuthState(false);
        }
      } else {
        localStorage.removeItem('skillfab_token');
        window.authToken = null;
        window.updateNavAuth(false);
      }
    } catch (_e) {
      // Network error — keep token and avoid flashing signed-out UI while auth is unknown.
      window.authStatus = 'error';
      window.applyAuthVisibility(window.authStatus);
      notifyAuthState(false);
    }
  };

  window.updateNavAuth = function(loggedIn: boolean): void {
    window.authStatus = loggedIn ? 'authenticated' : 'anonymous';
    window.applyAuthVisibility(window.authStatus);

    const profileLink = document.getElementById('nav-profile-link') as HTMLAnchorElement | null;
    if (loggedIn && window.currentUser) {
      const avatarEl = document.getElementById('nav-avatar');
      if (profileLink) {
        profileLink.href = '/users/' + encodeURIComponent(window.currentUser.username);
        profileLink.style.display = '';
      }
      if (avatarEl) {
        const name = window.currentUser.username || '?';
        avatarEl.textContent = name.charAt(0).toUpperCase();
        // Deterministic color from username
        const colors = [
          ['#6366f1', '#8b5cf6'], // indigo→violet
          ['#0ea5e9', '#6366f1'], // sky→indigo
          ['#10b981', '#0ea5e9'], // emerald→sky
          ['#f59e0b', '#ef4444'], // amber→red
          ['#ec4899', '#8b5cf6'], // pink→violet
          ['#14b8a6', '#6366f1'], // teal→indigo
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
        const [c1, c2] = colors[hash % colors.length];
        avatarEl.style.background = `linear-gradient(135deg, ${c1}, ${c2})`;
        avatarEl.style.boxShadow = `0 0 0 2px ${c1}4d`;
      }
      window.refreshNotificationBadge();
    } else if (profileLink) {
      profileLink.style.display = 'none';
    }

    notifyAuthState(loggedIn);
  };

  window.logout = async function(): Promise<void> {
    try {
      await fetch(window.API + '/api/auth/logout', {
        method: 'POST',
        headers: buildCoreAuthHeaders(),
        credentials: 'same-origin',
        keepalive: true,
      });
    } catch (_e) {
      // Local state is still cleared below; the next server check will reject stale UI.
    } finally {
      localStorage.removeItem('skillfab_token');
      window.authToken = null;
      window.currentUser = null;
      window.authStatus = 'anonymous';
      window.applyAuthVisibility(window.authStatus);
      window.location.href = '/';
    }
  };

  // User menu dropdown management
  window.toggleUserMenu = function(event?: Event): void {
    if (event) event.stopPropagation();
    const dropdown = document.getElementById('user-menu-dropdown');
    const chevron = document.getElementById('user-menu-chevron');
    const trigger = document.getElementById('user-menu-trigger');
    if (!dropdown) return;

    const isOpen = dropdown.classList.contains('show');
    if (isOpen) {
      dropdown.classList.remove('show');
      dropdown.setAttribute('aria-hidden', 'true');
      dropdown.setAttribute('inert', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (chevron) chevron.classList.remove('open');
      document.removeEventListener('click', window.closeUserMenuOnClickOutside);
    } else {
      dropdown.classList.add('show');
      dropdown.setAttribute('aria-hidden', 'false');
      dropdown.removeAttribute('inert');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (chevron) chevron.classList.add('open');
      // Delay adding listener to avoid immediate trigger
      setTimeout(function() {
        document.addEventListener('click', window.closeUserMenuOnClickOutside);
      }, 0);
    }
  };

  window.closeUserMenu = function(): void {
    const dropdown = document.getElementById('user-menu-dropdown');
    const chevron = document.getElementById('user-menu-chevron');
    const trigger = document.getElementById('user-menu-trigger');
    if (dropdown) {
      dropdown.classList.remove('show');
      dropdown.setAttribute('aria-hidden', 'true');
      dropdown.setAttribute('inert', '');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (chevron) chevron.classList.remove('open');
    document.removeEventListener('click', window.closeUserMenuOnClickOutside);
  };

  window.closeUserMenuOnClickOutside = function(event: Event): void {
    const menu = document.getElementById('nav-user-menu');
    if (menu && !menu.contains(event.target as Node)) {
      window.closeUserMenu();
    }
  };

  function parseSkillfabDate(dateStr: string | null | undefined): Date | null {
    const raw = String(dateStr || '').trim();
    if (!raw) return null;
    const normalized = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(raw)
      ? raw
      : raw.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Time formatting utilities
  window.timeAgo = function(dateStr: string): string {
    const date = parseSkillfabDate(dateStr);
    if (!date) return '-';
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
    return date.toLocaleDateString();
  };

  window.formatDateTime = function(dateStr: string | null | undefined, mode?: string): string {
    const date = parseSkillfabDate(dateStr);
    if (!date) return '-';
    if (mode === 'datetime') return date.toLocaleString();
    return date.toLocaleDateString();
  };

  // UI state helpers
  window.loadingStateHtml = function(message?: string): string {
    return '<div class="text-center py-12 text-zinc-500"><i class="fas fa-spinner fa-spin text-2xl mb-3"></i><p>' + (message || 'Loading...') + '</p></div>';
  };

  window.emptyStateHtml = function(icon: string, message: string, extraClass?: string): string {
    return '<div class="' + (extraClass || 'text-center py-16') + '"><i class="fas ' + (icon || 'fa-inbox') + ' text-4xl text-zinc-700 mb-3"></i><p class="text-zinc-500">' + (message || 'No data') + '</p></div>';
  };

  window.errorStateHtml = function(message: string, extraClass?: string): string {
    return '<p class="' + (extraClass || 'text-center py-8 text-red-400') + '">' + (message || 'Failed to load') + '</p>';
  };

  window.statusBadge = function(status: string): string {
    return window.issueStatusBadge(status);
  };

  window.refreshNotificationBadge = async function(): Promise<void> {
    if (!window.currentUser) return;
    try {
      const res = await fetch(window.API + '/api/notifications/unread-count', {
        headers: buildCoreAuthHeaders(),
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      let data: { success?: boolean; data?: { count?: number } };
      try { data = await res.json(); } catch { return; }
      const count = data.success && data.data ? (data.data.count || 0) : 0;
      const badge = document.getElementById('nav-bell-badge');
      if (!badge) return;
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.style.display = 'flex';
        badge.classList.remove('hidden');
      } else {
        badge.style.display = 'none';
        badge.classList.add('hidden');
      }
    } catch (_e) {
      // Ignore errors - badge is non-critical
    }
  };

  // Toast notifications
  window.showToast = function(msg: string, type?: string): void {
    type = type || 'success';
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl text-sm font-medium shadow-xl animate-in ' +
      (type === 'error' ? 'bg-red-900/90 text-red-200 border border-red-800/50' : 'bg-brand-900/90 text-brand-200 border border-brand-800/50');
    toast.innerHTML = '<i class="fas ' + (type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle') + ' mr-2"></i>' + escapeHtml(msg);
    document.body.appendChild(toast);
    setTimeout(function() {
      toast.remove();
    }, 3500);
  };
})();
